class LuxuryHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.timer = null;
    this.isPaused = false;
    this.isTransitioning = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.slides = Array.from(this.querySelectorAll('.luxury-hero__slide'));
    if (this.slides.length <= 1) {
      this.classList.add('luxury-hero--single-slide');
      return;
    }

    this.slideDuration = Math.round(parseFloat(this.dataset.duration)) || 6500;
    this.crossfadeDuration = Math.round(parseFloat(this.dataset.crossfade)) || 1000;
    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.reducedMotion) {
      this.classList.add('luxury-hero--reduced-motion');
      this.crossfadeDuration = 350;
    }

    this.style.setProperty('--luxury-slide-duration', `${this.slideDuration}ms`);
    this.style.setProperty('--luxury-crossfade', `${this.crossfadeDuration}ms`);

    this.paginationItems = Array.from(this.querySelectorAll('.luxury-hero__pagination-item'));
    this.prevButton = this.querySelector('.luxury-hero__arrow--prev');
    this.nextButton = this.querySelector('.luxury-hero__arrow--next');

    this.bindEvents();
    this.goTo(0, { immediate: true });

    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchmove', this.onTouchMove);
    this.removeEventListener('touchend', this.onTouchEnd);
  }

  bindEvents() {
    this.paginationItems.forEach((item) => {
      item.addEventListener('click', () => {
        const index = parseInt(item.dataset.index, 10);
        if (!Number.isNaN(index)) {
          this.goTo(index);
          this.resetAutoplay();
        }
      });
    });

    this.prevButton?.addEventListener('click', () => {
      this.prev();
      this.resetAutoplay();
    });

    this.nextButton?.addEventListener('click', () => {
      this.next();
      this.resetAutoplay();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.addEventListener('touchend', this.onTouchEnd);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pauseAutoplay();
    } else if (this.autoplayEnabled) {
      this.resumeAutoplay();
    }
  }

  onFocusIn(event) {
    if (event.target.closest('.luxury-hero__controls, .luxury-hero__cta')) {
      this.pauseAutoplay();
    }
  }

  onFocusOut(event) {
    if (!this.contains(event.relatedTarget)) {
      this.resumeAutoplay();
    }
  }

  onTouchStart(event) {
    if (event.touches.length !== 1) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchDeltaX = 0;
    this.pauseAutoplay();
  }

  onTouchMove(event) {
    if (event.touches.length !== 1) return;
    const deltaX = event.touches[0].clientX - this.touchStartX;
    const deltaY = event.touches[0].clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      event.preventDefault();
      this.touchDeltaX = deltaX;
    }
  }

  onTouchEnd() {
    const threshold = 50;
    if (this.touchDeltaX <= -threshold) {
      this.next();
    } else if (this.touchDeltaX >= threshold) {
      this.prev();
    }
    this.touchDeltaX = 0;
    this.resetAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    this.isPaused = false;
    this.classList.remove('is-paused');
    this.timer = window.setTimeout(() => {
      this.next();
      this.startAutoplay();
    }, this.slideDuration);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  pauseAutoplay() {
    this.isPaused = true;
    this.classList.add('is-paused');
    this.stopAutoplay();
  }

  resumeAutoplay() {
    if (!this.autoplayEnabled || this.isTransitioning) return;
    this.isPaused = false;
    this.classList.remove('is-paused');
    this.startAutoplay();
  }

  resetAutoplay() {
    if (!this.autoplayEnabled) return;
    this.stopAutoplay();
    this.startAutoplay();
    this.restartProgressAnimation();
  }

  restartProgressAnimation() {
    const activeProgress = this.querySelector('.luxury-hero__pagination-item.is-active .luxury-hero__pagination-progress');
    if (!activeProgress) return;
    activeProgress.style.animation = 'none';
    void activeProgress.offsetWidth;
    activeProgress.style.animation = '';
  }

  prev() {
    const index = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goTo(index);
  }

  next() {
    const index = (this.currentIndex + 1) % this.slides.length;
    this.goTo(index);
  }

  goTo(index, { immediate = false } = {}) {
    if (index === this.currentIndex && !immediate) return;
    if (this.isTransitioning && !immediate) return;

    const previousSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    this.isTransitioning = true;

    if (previousSlide && previousSlide !== nextSlide) {
      previousSlide.classList.remove('is-active');
      previousSlide.classList.add('is-leaving');
      window.requestAnimationFrame(() => {
        previousSlide.classList.add('is-leaving-out');
      });
      window.setTimeout(() => {
        previousSlide.classList.remove('is-leaving', 'is-leaving-out');
      }, this.crossfadeDuration);
    }

    this.slides.forEach((slide, i) => {
      if (slide === previousSlide && previousSlide !== nextSlide) return;
      slide.classList.toggle('is-active', i === index);
      slide.classList.remove('is-leaving', 'is-leaving-out');
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    if (previousSlide && previousSlide !== nextSlide) {
      previousSlide.setAttribute('aria-hidden', 'true');
    }

    this.paginationItems.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
      item.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.currentIndex = index;

    window.setTimeout(() => {
      this.isTransitioning = false;
      if (this.autoplayEnabled && !this.isPaused && !document.hidden) {
        this.restartProgressAnimation();
      }
    }, immediate ? 0 : this.crossfadeDuration);
  }
}

if (!customElements.get('luxury-hero-slider')) {
  customElements.define('luxury-hero-slider', LuxuryHeroSlider);
}
